// Hash-based deep-link routing (dependency-free — no react-router).
//
// Route grammar:
//   #/<cond>/<incidentId>                        e.g. #/C4/738908
//   #/<cond>/<incidentId>/source/<outletDomain>  e.g. #/C4/738908/source/miamiherald.com
//
// Empty / invalid segments degrade to null so an absent hash behaves exactly like
// the pre-routing app (the smoke test boots with no hash). Condition/stimulus
// changes are written with history.replaceState (no history spam); opening the
// source viewer uses a hash assignment (pushes an entry, so browser Back closes it).

import { CONDITIONS, type ConditionKey } from "./types";

export interface Route {
  cond: ConditionKey | null;
  id: number | null;
  source: string | null;
}

export const EMPTY_ROUTE: Route = { cond: null, id: null, source: null };

export function parseHash(hash: string): Route {
  const parts = (hash || "")
    .replace(/^#\/?/, "")
    .split("/")
    .filter(Boolean)
    .map((p) => { try { return decodeURIComponent(p); } catch { return p; } });
  const cond = parts[0] && Object.prototype.hasOwnProperty.call(CONDITIONS, parts[0])
    ? (parts[0] as ConditionKey) : null;
  const id = parts[1] && /^\d+$/.test(parts[1]) ? Number(parts[1]) : null;
  const source = parts[2] === "source" && parts[3] ? parts[3] : null;
  return { cond, id, source };
}

export function formatHash(cond: ConditionKey, id: number, source?: string | null): string {
  return `#/${cond}/${id}` + (source ? `/source/${encodeURIComponent(source)}` : "");
}

/** Current route from location.hash (EMPTY_ROUTE outside a window, e.g. SSR). */
export function readRoute(): Route {
  if (typeof window === "undefined" || !window.location) return EMPTY_ROUTE;
  return parseHash(window.location.hash);
}

/** Sync the hash WITHOUT adding a history entry (condition/stimulus switches). */
export function replaceRoute(cond: ConditionKey, id: number, source?: string | null): void {
  if (typeof window === "undefined" || !window.history || !window.location) return;
  const h = formatHash(cond, id, source);
  if (window.location.hash === h) return;
  try { window.history.replaceState(null, "", h); } catch { /* never block the UI on routing */ }
}

/** Navigate WITH a new history entry (opening the source viewer — Back closes it). */
export function pushRoute(cond: ConditionKey, id: number, source?: string | null): void {
  if (typeof window === "undefined" || !window.location) return;
  const h = formatHash(cond, id, source);
  if (window.location.hash === h) return;
  try { window.location.hash = h; } catch { /* never block the UI on routing */ }
}

/** Subscribe to back/forward + hash edits; returns an unsubscribe. */
export function onRouteChange(fn: (r: Route) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => fn(readRoute());
  window.addEventListener("hashchange", handler);
  return () => window.removeEventListener("hashchange", handler);
}
